import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Send, Mail, MessageSquare, Info, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const MessagingCenter = () => {
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSMS, setSendSMS] = useState(false);
  const [sending, setSending] = useState(false);
  const [configStatus, setConfigStatus] = useState(null);

  useEffect(() => {
    loadClients();
    loadConfigStatus();
  }, []);

  const loadClients = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messaging/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(response.data);
    } catch (error) {
      console.error('Load clients error:', error);
      toast.error('Failed to load clients');
    }
  };

  const loadConfigStatus = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messaging/config-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfigStatus(response.data);
    } catch (error) {
      console.error('Load config error:', error);
    }
  };

  const handleSelectAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(c => c.id));
    }
  };

  const handleToggleClient = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  const handleSendMessage = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select at least one client');
      return;
    }

    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);

    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/messaging/send-message`,
        {
          clientIds: selectedClients,
          subject,
          message,
          sendEmail,
          sendSMS,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(
        `Message sent! Emails: ${response.data.results.emailsSent}, SMS: ${response.data.results.smsSent}`
      );

      // Reset form
      setSelectedClients([]);
      setSubject('');
      setMessage('');
    } catch (error) {
      console.error('Send message error:', error);
      toast.error(error.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <div className="flex items-center space-x-3">
          <Send size={32} className="text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold">Messaging Center</h1>
            <p className="text-gray-600">Send messages to your clients</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      {configStatus && (!configStatus.email.configured || !configStatus.sms.configured) && (
        <div className="card mb-6 bg-yellow-50 border-2 border-yellow-200">
          <div className="flex items-start space-x-3">
            <Info size={24} className="text-yellow-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-900 mb-2">Email or SMS Not Configured</h3>
              <p className="text-sm text-yellow-800 mb-3">
                To send messages to clients, you need to configure your email and SMS providers first.
              </p>
              <Link
                to="/admin"
                className="inline-flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm"
              >
                <span>Go to Settings</span>
                <ExternalLink size={16} />
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Selection */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Select Recipients</h2>
              <button
                onClick={handleSelectAll}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                {selectedClients.length === clients.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {clients.map((client) => (
                <label
                  key={client.id}
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedClients.includes(client.id)}
                    onChange={() => handleToggleClient(client.id)}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                  <div className="flex-1">
                    <p className="font-medium">
                      {client.first_name} {client.last_name}
                    </p>
                    <p className="text-sm text-gray-600">{client.email}</p>
                    {client.phone && (
                      <p className="text-sm text-gray-600">{client.phone}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-600">
                <strong>{selectedClients.length}</strong> of <strong>{clients.length}</strong> clients selected
              </p>
            </div>
          </div>
        </div>

        {/* Message Composer */}
        <div className="lg:col-span-2">
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Compose Message</h2>

            {/* Delivery Method */}
            <div className="mb-4">
              <label className="label">Delivery Method</label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                  <Mail size={20} />
                  <span>Email</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={sendSMS}
                    onChange={(e) => setSendSMS(e.target.checked)}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                  <MessageSquare size={20} />
                  <span>SMS</span>
                </label>
              </div>
            </div>

            {/* Subject (Email only) */}
            {sendEmail && (
              <div className="mb-4">
                <label className="label">Subject (Email only)</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Special Promotion"
                  className="input"
                />
              </div>
            )}

            {/* Message */}
            <div className="mb-4">
              <label className="label">Message *</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={8}
                placeholder="Enter your message here..."
                className="input resize-none"
              />
              <p className="text-sm text-gray-500 mt-1">
                {message.length} characters
              </p>
            </div>

            {/* Send Button */}
            <button
              onClick={handleSendMessage}
              disabled={sending || selectedClients.length === 0 || !message.trim()}
              className="w-full btn btn-primary flex items-center justify-center space-x-2"
            >
              <Send size={20} />
              <span>{sending ? 'Sending...' : `Send to ${selectedClients.length} client(s)`}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingCenter;
